import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { combineLatest, EMPTY, merge, switchMap, tap } from 'rxjs';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { type ThemeColorName } from '@examdojo/styles';
import type { ElementLabelConfig, PointFace, SnappingConfig, ThemeColorWithShade } from '@examdojo/core/widget';
import { parseThemeColor, THEME_COLORS_WITH_SHADES } from '@examdojo/core/widget';
import type {
  ElementDef,
  ElementDisplayConfig,
  FeatureElementDef,
  FunctionElementDef,
  LabelElementParams,
  LineElementParams,
  PointElementParams,
  PrimitiveElementDef,
  ValueExpression,
} from '../../graphpad.model';
import { isFeatureElementDef, isFunctionElementDef, isPrimitiveElementDef } from '../../graphpad.model';
import {
  DUAL_PARENT_FEATURES,
  getDisplayStyleMeta,
  getElementValueInfo,
  getFeatureDefaultParams,
  getFunctionMeta,
  getStyleKeyForProperty,
  LABEL_CONTENT_VARIANT_OPTIONS,
  LABEL_POSITION_OPTIONS,
  LABEL_TEXT_ALIGN_OPTIONS,
  LABEL_VARIANT_OPTIONS,
  POINT_FACE_OPTIONS,
  type DisplayStyleMeta,
} from '../graphpad-editor.model';

interface SelectOption {
  label: string;
  value: string;
}

interface ParamCtrlEntry {
  key: string;
  ctrl: FormControl<number>;
  min: number;
  max: number;
  step: number;
}

interface ExprParamCtrlEntry {
  key: string;
  ctrl: FormControl<string>;
}

interface ValueGroup {
  label: string;
  paths: Array<{ display: string; copyPath: string }>;
  pattern?: string;
}

@Component({
  selector: 'dojo-element-editor',
  standalone: true,
  imports: [ReactiveFormsModule, MatSlideToggleModule, NumberInputComponent, TextInputComponent, SingleSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './element-editor.component.html',
})
export class ElementEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    this.syncFromInputs().pipe(takeUntilDestroyed()).subscribe();

    // Number param controls — re-subscribes when entries signal changes
    toObservable(this.numberParamEntries)
      .pipe(
        switchMap((entries) =>
          entries.length
            ? merge(
                ...entries.map((e) =>
                  e.ctrl.valueChanges.pipe(tap((value) => this.paramsChange.emit({ [e.key]: value }))),
                ),
              )
            : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Expression param controls — re-subscribes when entries signal changes
    toObservable(this.expressionParamEntries)
      .pipe(
        switchMap((entries) =>
          entries.length
            ? merge(
                ...entries.map((e) =>
                  e.ctrl.valueChanges.pipe(
                    tap((value) => {
                      const num = Number(value);
                      this.paramsChange.emit({ [e.key]: isNaN(num) ? value : num });
                    }),
                  ),
                ),
              )
            : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Display control subscriptions
    this.strokeWidthCtrl.valueChanges
      .pipe(
        tap((v) => this.emitStyleChange('strokeWidth', v)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.dashCtrl.valueChanges
      .pipe(
        tap((v) => this.emitStyleChange('dash', v)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.sizeCtrl.valueChanges
      .pipe(
        tap((v) => this.emitStyleChange('size', v)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.fillOpacityCtrl.valueChanges
      .pipe(
        tap((v) => this.emitStyleChange('fillOpacity', v)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.polyStrokeWidthCtrl.valueChanges
      .pipe(
        tap((v) => {
          const display = this.elementDisplay() ?? {};
          this.displayChange.emit({
            ...display,
            polygon: { ...(display.polygon ?? {}), strokeWidth: v },
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Label control subscriptions
    this.labelTextCtrl.valueChanges
      .pipe(
        tap((v) => {
          const text = v ? v.replace(/\\n/g, '\n') : undefined;
          this.emitLabelChange({ text });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.labelFontSizeCtrl.valueChanges
      .pipe(
        tap((v) => this.emitLabelChange({ fontSize: v ?? undefined })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.labelPrecisionCtrl.valueChanges
      .pipe(
        tap((v) => this.emitLabelChange({ precision: v ?? undefined })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.labelOffsetXCtrl.valueChanges
      .pipe(
        tap((v) => {
          const current = this.getLabelConfig()?.offset ?? [0, 0];
          this.emitLabelChange({ offset: [v, current[1]] });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.labelOffsetYCtrl.valueChanges
      .pipe(
        tap((v) => {
          const current = this.getLabelConfig()?.offset ?? [0, 0];
          this.emitLabelChange({ offset: [current[0], v] });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Primitive param subscriptions
    this.primLabelTextCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ text: v.replace(/\\n/g, '\n') })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLabelPrecisionCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ precision: v ?? undefined })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLabelFontSizeCtrl.valueChanges
      .pipe(
        tap((v) => this.onPrimLabelDisplayChange('fontSize', v ?? undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLabelPosXCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LabelElementParams;
            this.paramsChange.emit({ position: { ...(params.position ?? { x: 0, y: 0 }), x: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLabelPosYCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LabelElementParams;
            this.paramsChange.emit({ position: { ...(params.position ?? { x: 0, y: 0 }), y: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primPointXCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ x: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primPointYCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ y: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primPointNameCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ name: v || undefined })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLineFromXCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LineElementParams;
            this.paramsChange.emit({ from: { ...(params.from ?? { x: 0, y: 0 }), x: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLineFromYCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LineElementParams;
            this.paramsChange.emit({ from: { ...(params.from ?? { x: 0, y: 0 }), y: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLineToXCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LineElementParams;
            this.paramsChange.emit({ to: { ...(params.to ?? { x: 0, y: 0 }), x: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLineToYCtrl.valueChanges
      .pipe(
        tap((v) => {
          const def = this.elementDef();
          if (isPrimitiveElementDef(def)) {
            const params = def.params as LineElementParams;
            this.paramsChange.emit({ to: { ...(params.to ?? { x: 0, y: 0 }), y: v } });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.primLineNameCtrl.valueChanges
      .pipe(
        tap((v) => this.paramsChange.emit({ name: v || undefined })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Snapping control subscriptions
    this.snapGridSizeXCtrl.valueChanges
      .pipe(
        tap((v) => this.emitSnapGridChange({ sizeX: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.snapGridSizeYCtrl.valueChanges
      .pipe(
        tap((v) => this.emitSnapGridChange({ sizeY: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.snapAttractorsCtrl.valueChanges
      .pipe(
        tap((v) => {
          const ids = v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          this.emitSnapAttractorsChange({ attractors: ids });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.snapDistanceCtrl.valueChanges
      .pipe(
        tap((v) => this.emitSnapAttractorsChange({ distance: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.snapSnatchDistanceCtrl.valueChanges
      .pipe(
        tap((v) => this.emitSnapAttractorsChange({ snatchDistance: v })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  readonly elementId = input.required<string>();
  readonly elementDef = input.required<ElementDef>();
  readonly elementDisplay = input<ElementDisplayConfig>();
  readonly functionElementIds = input<string[]>([]);
  readonly allElementIds = input<string[]>([]);

  readonly paramsChange = output<Record<string, unknown>>();
  readonly displayChange = output<ElementDisplayConfig>();

  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly faceOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...POINT_FACE_OPTIONS.map((f) => ({ label: f, value: f })),
  ];

  readonly labelPositionOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...LABEL_POSITION_OPTIONS.map((p) => ({ label: p, value: p })),
  ];

  readonly labelVariantOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...LABEL_VARIANT_OPTIONS.map((v) => ({ label: v, value: v })),
  ];

  readonly labelContentVariantOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...LABEL_CONTENT_VARIANT_OPTIONS.map((v) => ({ label: v, value: v })),
  ];

  readonly labelTextAlignOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...LABEL_TEXT_ALIGN_OPTIONS.map((v) => ({ label: v, value: v })),
  ];

  readonly labelAnchorAtOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'start', value: 'start' },
    { label: 'middle', value: 'middle' },
    { label: 'end', value: 'end' },
  ];

  // --- Base form controls ---
  readonly parentCtrl = new FormControl<string>('', { nonNullable: true });
  readonly parent2Ctrl = new FormControl<string>('', { nonNullable: true });
  readonly colorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly secondaryColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly strokeWidthCtrl = new FormControl<number>(2, { nonNullable: true });
  readonly dashCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly sizeCtrl = new FormControl<number>(4, { nonNullable: true });
  readonly fillOpacityCtrl = new FormControl<number>(0.3, { nonNullable: true });

  // --- Point extras ---
  readonly faceCtrl = new FormControl<string>('', { nonNullable: true });
  readonly strokeColorCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Polygon extras ---
  readonly polyStrokeColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly polyStrokeWidthCtrl = new FormControl<number>(1, { nonNullable: true });

  // --- Label config ---
  readonly labelPositionCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelVariantCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelContentVariantCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelTextCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelFontSizeCtrl = new FormControl<number | null>(null);
  readonly labelPrecisionCtrl = new FormControl<number | null>(null);
  readonly labelTextAlignCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelOffsetXCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly labelOffsetYCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly labelAnchorAtCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Snapping controls (point elements) ---
  readonly snapGridSizeXCtrl = new FormControl<number>(1, { nonNullable: true });
  readonly snapGridSizeYCtrl = new FormControl<number>(1, { nonNullable: true });
  readonly snapAttractorsCtrl = new FormControl<string>('', { nonNullable: true });
  readonly snapDistanceCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly snapSnatchDistanceCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly snapUnitCtrl = new FormControl<string>('user', { nonNullable: true });

  readonly snapUnitOptions: SelectOption[] = [
    { label: 'User', value: 'user' },
    { label: 'Screen', value: 'screen' },
  ];

  // --- Primitive param controls ---
  readonly primExpressionModes = signal<Set<string>>(new Set());
  private readonly primExprCtrls = new Map<string, FormControl<string>>();
  readonly primLabelTextCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLabelPrecisionCtrl = new FormControl<number | null>(null);
  readonly primLabelOfCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLabelPosXCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLabelPosYCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLabelVariantCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLabelPositionCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLabelTextAlignCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLabelFontSizeCtrl = new FormControl<number | null>(null);
  readonly primPointXCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primPointYCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primPointNameCtrl = new FormControl<string>('', { nonNullable: true });
  readonly primLineFromXCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLineFromYCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLineToXCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLineToYCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly primLineNameCtrl = new FormControl<string>('', { nonNullable: true });

  private readonly selectCtrls = new Map<string, FormControl<string>>();

  readonly isFunction = computed(() => isFunctionElementDef(this.elementDef()));
  readonly isFeature = computed(() => isFeatureElementDef(this.elementDef()));
  readonly isPrimitive = computed(() => isPrimitiveElementDef(this.elementDef()));

  readonly parentOptions = computed<SelectOption[]>(() =>
    this.functionElementIds().map((id) => ({ label: id, value: id })),
  );

  readonly allElementIdOptions = computed<SelectOption[]>(() =>
    this.allElementIds()
      .filter((id) => id !== this.elementId())
      .map((id) => ({ label: id, value: id })),
  );

  readonly isDualParent = computed(() => {
    const def = this.elementDef();
    return isFeatureElementDef(def) && DUAL_PARENT_FEATURES.includes(def.type);
  });

  readonly primitiveType = computed(() => {
    const def = this.elementDef();
    return isPrimitiveElementDef(def) ? def.type : null;
  });

  // --- Params section ---

  readonly numberParamEntries = signal<ParamCtrlEntry[]>([]);
  readonly expressionParamEntries = signal<ExprParamCtrlEntry[]>([]);
  readonly booleanParamKeys = signal<string[]>([]);
  readonly selectParamKeys = signal<string[]>([]);
  readonly fnExpressionModes = signal<Set<string>>(new Set());

  // --- Display section ---

  private readonly styleMeta = computed<DisplayStyleMeta>(() => {
    const def = this.elementDef();
    const category = this.isFunction() ? 'function' : this.isPrimitive() ? 'primitive' : 'feature';
    return getDisplayStyleMeta(def.type, category);
  });

  readonly showStrokeWidth = computed(() => this.styleMeta().properties.includes('strokeWidth'));
  readonly showDash = computed(() => this.styleMeta().properties.includes('dash'));
  readonly showSize = computed(() => this.styleMeta().properties.includes('size'));
  readonly showFillOpacity = computed(() => this.styleMeta().properties.includes('fillOpacity'));

  readonly showSecondaryColor = computed(() => {
    const meta = this.styleMeta();
    return meta.properties.includes('color') && (meta.secondaryKeys?.length ?? 0) > 0;
  });

  readonly showPointExtras = computed(() => {
    const meta = this.styleMeta();
    return meta.primaryKey === 'point' && meta.properties.includes('face');
  });

  readonly showPointFixed = computed(() => this.styleMeta().properties.includes('fixed'));

  readonly showLineExtras = computed(() => {
    const meta = this.styleMeta();
    return (
      meta.primaryKey === 'line' &&
      (meta.properties.includes('straightFirst') || meta.properties.includes('straightLast'))
    );
  });

  readonly showPolygonExtras = computed(() => {
    const meta = this.styleMeta();
    return meta.primaryKey === 'polygon' && meta.properties.includes('strokeColor');
  });

  readonly showLabelSection = computed(() => this.styleMeta().hasLabel === true);

  /** Which display style key owns the label config (defaults to primaryKey). */
  private readonly labelStyleKey = computed(() => this.styleMeta().labelKey ?? this.styleMeta().primaryKey);

  // Computed display values for toggles
  readonly fixedValue = computed(() => {
    const display = this.elementDisplay();
    return (display?.point as Record<string, unknown> | undefined)?.['fixed'] === true;
  });

  readonly showGrabHandleValue = computed(() => {
    const display = this.elementDisplay();
    return (display?.point as Record<string, unknown> | undefined)?.['showGrabHandle'] === true;
  });

  readonly arrowFirstValue = computed(() => {
    const display = this.elementDisplay();
    const arrows = (display?.line as Record<string, unknown> | undefined)?.['arrows'] as
      | Record<string, unknown>
      | undefined;
    return arrows?.['first'] === true;
  });

  readonly arrowLastValue = computed(() => {
    const display = this.elementDisplay();
    const arrows = (display?.line as Record<string, unknown> | undefined)?.['arrows'] as
      | Record<string, unknown>
      | undefined;
    return arrows?.['last'] === true;
  });

  readonly straightFirstValue = computed(() => {
    const display = this.elementDisplay();
    return (display?.line as Record<string, unknown> | undefined)?.['straightFirst'] === true;
  });

  readonly straightLastValue = computed(() => {
    const display = this.elementDisplay();
    return (display?.line as Record<string, unknown> | undefined)?.['straightLast'] === true;
  });

  readonly showSnapping = computed(() => this.styleMeta().primaryKey === 'point');

  readonly valueGroups = computed<ValueGroup[]>(() => {
    const def = this.elementDef();
    const category = this.isFunction() ? 'function' : this.isPrimitive() ? 'primitive' : 'feature';
    const info = getElementValueInfo(def.type, category);
    const id = this.elementId();
    const groups: ValueGroup[] = [];

    if (info.params.length > 0) {
      groups.push({
        label: 'Parameters',
        paths: info.params.map((key) => ({ display: `${id}.${key}`, copyPath: `${id}.${key}` })),
      });
    }

    if (info.computed.length > 0) {
      groups.push({
        label: 'Values',
        paths: info.computed.map((key) => ({ display: `${id}.${key}`, copyPath: `${id}.${key}` })),
      });
    }

    for (const col of info.collections) {
      const collectionLabel = col.name || def.type;
      const prefix = col.name ? `${id}.${col.name}` : id;
      const patternProps = col.properties.join(', ');
      const pattern = `${prefix}.{i}.${patternProps}  (i=0..${col.maxItems - 1})`;

      // Show count (if exists in scalar computed) + first item as representative
      const paths: Array<{ display: string; copyPath: string }> = [];
      paths.push(
        ...col.properties.map((prop) => ({
          display: `${prefix}.0.${prop}`,
          copyPath: `${prefix}.0.${prop}`,
        })),
      );

      groups.push({ label: collectionLabel, paths, pattern });
    }

    return groups;
  });

  readonly snapGridEnabled = computed(() => {
    const display = this.elementDisplay();
    const snapping = (display?.point as Record<string, unknown> | undefined)?.['snapping'] as
      | SnappingConfig
      | undefined;
    return snapping?.grid?.enabled === true;
  });

  readonly hasAttractors = computed(() => {
    const val = this.snapAttractorsCtrl.value;
    return val.trim().length > 0;
  });

  readonly labelShowValue = computed(() => {
    const display = this.elementDisplay();
    const key = this.labelStyleKey();
    const section = display?.[key] as Record<string, unknown> | undefined;
    const label = section?.['label'];
    if (label === true) {
      return true;
    }
    if (typeof label === 'object' && label !== null) {
      return (label as ElementLabelConfig).show !== false;
    }
    return false;
  });

  readonly labelPopupValue = computed(() => {
    const label = this.getLabelConfig();
    return label?.popup === true;
  });

  /** Known select params and their options */
  private readonly SELECT_PARAM_OPTIONS: Record<string, string[]> = {
    behavior: ['translate', 'translate-x', 'translate-y'],
    arrowDirection: ['start', 'end', 'both', 'none'],
  };

  // --- Parent change (features) ---

  onParentChange(parentId: unknown): void {
    if (this.isDualParent()) {
      this.paramsChange.emit({ of: [parentId as string, this.parent2Ctrl.value] });
    } else {
      this.paramsChange.emit({ of: parentId as string });
    }
  }

  onParent2Change(parentId: unknown): void {
    this.paramsChange.emit({ of: [this.parentCtrl.value, parentId as string] });
  }

  // --- Param field change ---

  onParamFieldChange(key: string, value: unknown): void {
    this.paramsChange.emit({ [key]: value });
  }

  getBooleanParamValue(key: string): boolean {
    const def = this.elementDef();
    const params = isFunctionElementDef(def) || isFeatureElementDef(def) ? def.params ?? {} : {};
    return !!params[key];
  }

  getSelectCtrl(key: string): FormControl<string> {
    if (!this.selectCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.selectCtrls.set(key, ctrl);
    }
    return this.selectCtrls.get(key)!;
  }

  getSelectOptions(key: string): SelectOption[] {
    return (this.SELECT_PARAM_OPTIONS[key] ?? []).map((o) => ({ label: o, value: o }));
  }

  // --- Display changes ---

  onColorChange(color: unknown): void {
    const display = this.elementDisplay() ?? {};
    const colorValue = ((color as string) || undefined) as ThemeColorWithShade | undefined;
    const key = this.styleMeta().primaryKey;

    if (key === 'polygon') {
      this.displayChange.emit({
        ...display,
        polygon: { ...(display.polygon ?? {}), fillColor: colorValue },
      });
    } else if (key === 'label') {
      // Labels don't support shading — strip shade to base color name
      const labelColor = colorValue ? (parseThemeColor(colorValue).name as ThemeColorName) : undefined;
      this.displayChange.emit({
        ...display,
        label: { ...(display.label ?? {}), color: labelColor },
      });
    } else {
      this.displayChange.emit({
        ...display,
        [key]: { ...(display[key] ?? {}), color: colorValue },
      });
    }
  }

  onSecondaryColorChange(color: unknown): void {
    const display = this.elementDisplay() ?? {};
    const colorValue = ((color as string) || undefined) as ThemeColorWithShade | undefined;
    const key = getStyleKeyForProperty('color', this.styleMeta());
    this.displayChange.emit({
      ...display,
      [key]: { ...(display[key] ?? {}), color: colorValue },
    });
  }

  // --- Point extras ---

  onPointFaceChange(face: unknown): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      point: { ...(display.point ?? {}), face: ((face as string) || undefined) as PointFace | undefined },
    });
  }

  onFixedChange(fixed: boolean): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      point: { ...(display.point ?? {}), fixed },
    });
  }

  onShowGrabHandleChange(show: boolean): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      point: { ...(display.point ?? {}), showGrabHandle: show },
    });
  }

  onPointStrokeColorChange(color: unknown): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      point: {
        ...(display.point ?? {}),
        strokeColor: ((color as string) || undefined) as ThemeColorWithShade | undefined,
      },
    });
  }

  // --- Line extras ---

  onArrowChange(end: 'first' | 'last', enabled: boolean): void {
    const display = this.elementDisplay() ?? {};
    const line = display.line ?? {};
    const arrows = (line as Record<string, unknown>)['arrows'] as Record<string, unknown> | undefined;
    this.displayChange.emit({
      ...display,
      line: {
        ...line,
        arrows: { ...(arrows ?? {}), [end]: enabled },
      },
    });
  }

  onStraightFirstChange(value: boolean): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      line: { ...(display.line ?? {}), straightFirst: value },
    });
  }

  onStraightLastChange(value: boolean): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      line: { ...(display.line ?? {}), straightLast: value },
    });
  }

  // --- Polygon extras ---

  onPolygonStrokeColorChange(color: unknown): void {
    const display = this.elementDisplay() ?? {};
    this.displayChange.emit({
      ...display,
      polygon: {
        ...(display.polygon ?? {}),
        strokeColor: ((color as string) || undefined) as ThemeColorWithShade | undefined,
      },
    });
  }

  // --- Label section ---

  onLabelShowChange(show: boolean): void {
    if (show) {
      this.emitLabelChange({ show: true });
    } else {
      // Set label to false to hide
      const display = this.elementDisplay() ?? {};
      const key = this.labelStyleKey();
      this.displayChange.emit({
        ...display,
        [key]: { ...(display[key] ?? {}), label: false },
      });
    }
  }

  onLabelFieldChange(field: string, value: unknown): void {
    const val = typeof value === 'string' ? value || undefined : value;
    this.emitLabelChange({ [field]: val });
  }

  // --- Snapping ---

  onSnapGridEnabledChange(enabled: boolean): void {
    const display = this.elementDisplay() ?? {};
    const point = (display.point ?? {}) as Record<string, unknown>;
    const snapping = (point['snapping'] ?? {}) as SnappingConfig;
    this.displayChange.emit({
      ...display,
      point: {
        ...point,
        snapping: {
          ...snapping,
          grid: { ...(snapping.grid ?? {}), enabled },
        },
      },
    });
  }

  onSnapUnitChange(unit: unknown): void {
    const display = this.elementDisplay() ?? {};
    const point = (display.point ?? {}) as Record<string, unknown>;
    const snapping = (point['snapping'] ?? {}) as SnappingConfig;
    this.displayChange.emit({
      ...display,
      point: {
        ...point,
        snapping: {
          ...snapping,
          attractors: { ...(snapping.attractors ?? { attractors: [] }), unit: unit as 'user' | 'screen' },
        },
      },
    });
  }

  // --- Clipboard ---

  copyToClipboard(path: string): void {
    navigator.clipboard.writeText(`{${path}}`);
  }

  // --- Primitive params ---

  onPrimLabelOfChange(of: unknown): void {
    this.paramsChange.emit({ of: (of as string) || undefined });
  }

  onPrimLabelDisplayChange(property: string, value: unknown): void {
    const display = this.elementDisplay() ?? {};
    const val = typeof value === 'string' ? value || undefined : value;
    this.displayChange.emit({
      ...display,
      label: { ...(display.label ?? {}), [property]: val },
    });
  }

  // --- Primitive position expression support ---

  getPrimExprCtrl(field: string): FormControl<string> {
    if (!this.primExprCtrls.has(field)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.primExprCtrls.set(field, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((v) => {
            const num = Number(v);
            this.emitPrimPositionValue(field, isNaN(num) ? v : num);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.primExprCtrls.get(field)!;
  }

  onPrimSwitchToExpression(field: string): void {
    const def = this.elementDef();
    if (!isPrimitiveElementDef(def)) {
      return;
    }

    let currentValue = 0;
    switch (field) {
      case 'fromX':
        currentValue = this.primLineFromXCtrl.value;
        break;
      case 'fromY':
        currentValue = this.primLineFromYCtrl.value;
        break;
      case 'toX':
        currentValue = this.primLineToXCtrl.value;
        break;
      case 'toY':
        currentValue = this.primLineToYCtrl.value;
        break;
      case 'pointX':
        currentValue = this.primPointXCtrl.value;
        break;
      case 'pointY':
        currentValue = this.primPointYCtrl.value;
        break;
      case 'posX':
        currentValue = this.primLabelPosXCtrl.value;
        break;
      case 'posY':
        currentValue = this.primLabelPosYCtrl.value;
        break;
    }
    this.emitPrimPositionValue(field, String(currentValue));
  }

  onPrimSwitchToNumber(field: string): void {
    this.emitPrimPositionValue(field, 0);
  }

  onSwitchToExpression(key: string): void {
    const def = this.elementDef();
    if (!isFunctionElementDef(def)) {
      return;
    }
    const params = def.params ?? {};
    const currentValue = params[key];
    // Convert current numeric value to string expression
    const strValue = currentValue !== null && currentValue !== undefined ? String(currentValue) : '0';
    this.paramsChange.emit({ [key]: strValue });
  }

  onSwitchToNumber(key: string): void {
    const def = this.elementDef();
    if (!isFunctionElementDef(def)) {
      return;
    }
    const meta = getFunctionMeta(def.type);
    // Emit the default numeric value for the param
    const defaultValue = meta?.defaultParams[key] ?? 0;
    this.paramsChange.emit({ [key]: defaultValue });
  }

  private emitPrimPositionValue(field: string, value: ValueExpression): void {
    const def = this.elementDef();
    if (!isPrimitiveElementDef(def)) {
      return;
    }

    switch (field) {
      case 'fromX': {
        const p = def.params as LineElementParams;
        this.paramsChange.emit({ from: { ...(p.from ?? { x: 0, y: 0 }), x: value } });
        break;
      }
      case 'fromY': {
        const p = def.params as LineElementParams;
        this.paramsChange.emit({ from: { ...(p.from ?? { x: 0, y: 0 }), y: value } });
        break;
      }
      case 'toX': {
        const p = def.params as LineElementParams;
        this.paramsChange.emit({ to: { ...(p.to ?? { x: 0, y: 0 }), x: value } });
        break;
      }
      case 'toY': {
        const p = def.params as LineElementParams;
        this.paramsChange.emit({ to: { ...(p.to ?? { x: 0, y: 0 }), y: value } });
        break;
      }
      case 'pointX':
        this.paramsChange.emit({ x: value });
        break;
      case 'pointY':
        this.paramsChange.emit({ y: value });
        break;
      case 'posX': {
        const p = def.params as LabelElementParams;
        this.paramsChange.emit({ position: { ...(p.position ?? { x: 0, y: 0 }), x: value } });
        break;
      }
      case 'posY': {
        const p = def.params as LabelElementParams;
        this.paramsChange.emit({ position: { ...(p.position ?? { x: 0, y: 0 }), y: value } });
        break;
      }
    }
  }

  // --- Private helpers ---

  private getLabelConfig(): ElementLabelConfig | undefined {
    const display = this.elementDisplay();
    const key = this.labelStyleKey();
    const section = display?.[key] as Record<string, unknown> | undefined;
    const label = section?.['label'];
    if (typeof label === 'object' && label !== null) {
      return label as ElementLabelConfig;
    }
    return undefined;
  }

  private emitLabelChange(partial: Partial<ElementLabelConfig>): void {
    const display = this.elementDisplay() ?? {};
    const key = this.labelStyleKey();
    const section = (display[key] ?? {}) as Record<string, unknown>;
    const currentLabel = typeof section['label'] === 'object' && section['label'] ? section['label'] : {};
    this.displayChange.emit({
      ...display,
      [key]: {
        ...section,
        label: { ...currentLabel, show: true, ...partial },
      },
    });
  }

  private syncFromInputs() {
    return combineLatest([toObservable(this.elementDef), toObservable(this.elementDisplay)]).pipe(
      tap(([def, display]) => {
        if (isFunctionElementDef(def)) {
          this.buildFunctionParamCtrls(def);
        } else if (isFeatureElementDef(def)) {
          this.syncFeatureParentCtrls(def);
          this.buildFeatureParamCtrls(def);
        } else if (isPrimitiveElementDef(def)) {
          this.buildPrimitiveParamCtrls(def);
        }

        this.syncDisplayCtrls(def, display);
      }),
    );
  }

  private buildFunctionParamCtrls(def: FunctionElementDef): void {
    const meta = getFunctionMeta(def.type);
    if (!meta) {
      this.numberParamEntries.set([]);
      this.expressionParamEntries.set([]);
      this.booleanParamKeys.set([]);
      this.selectParamKeys.set([]);
      this.fnExpressionModes.set(new Set());
      return;
    }

    const params: Record<string, ValueExpression> = { ...meta.defaultParams, ...(def.params ?? {}) };
    const numberEntries: ParamCtrlEntry[] = [];
    const exprEntries: ExprParamCtrlEntry[] = [];
    const exprModes = new Set<string>();

    for (const key of Object.keys(meta.defaultParams)) {
      const currentValue = params[key];
      const range = meta.defaultRanges[key] ?? [-100, 100];

      if (typeof currentValue === 'string') {
        // Expression mode
        exprModes.add(key);
        const ctrl = new FormControl<string>(currentValue, { nonNullable: true });
        exprEntries.push({ key, ctrl });
      } else {
        // Number mode
        const ctrl = new FormControl<number>(currentValue ?? 0, { nonNullable: true });
        numberEntries.push({ key, ctrl, min: range[0], max: range[1], step: 0.1 });
      }
    }

    this.numberParamEntries.set(numberEntries);
    this.expressionParamEntries.set(exprEntries);
    this.booleanParamKeys.set([]);
    this.selectParamKeys.set([]);
    this.fnExpressionModes.set(exprModes);
  }

  private syncFeatureParentCtrls(def: FeatureElementDef): void {
    const params = def.params ?? {};
    const ofValue = params['of'];
    if (Array.isArray(ofValue)) {
      this.parentCtrl.setValue((ofValue as string[])[0] ?? '', { emitEvent: false });
      this.parent2Ctrl.setValue((ofValue as string[])[1] ?? '', { emitEvent: false });
    } else if (typeof ofValue === 'string') {
      this.parentCtrl.setValue(ofValue, { emitEvent: false });
    }
  }

  private buildFeatureParamCtrls(def: FeatureElementDef): void {
    const defaults = getFeatureDefaultParams(def.type);
    const params = def.params ?? {};
    const merged = { ...defaults, ...params };

    const numberEntries: ParamCtrlEntry[] = [];
    const exprEntries: ExprParamCtrlEntry[] = [];
    const boolKeys: string[] = [];
    const selKeys: string[] = [];

    // Iterate over defaults keys (excludes 'of') to determine field type
    for (const key of Object.keys(defaults)) {
      const defaultValue = defaults[key];
      const currentValue = merged[key];

      if (key in this.SELECT_PARAM_OPTIONS) {
        selKeys.push(key);
        const ctrl = this.getSelectCtrl(key);
        ctrl.setValue((currentValue as string) ?? (defaultValue as string) ?? '', { emitEvent: false });
      } else if (typeof defaultValue === 'boolean') {
        boolKeys.push(key);
      } else if (typeof defaultValue === 'number') {
        // ValueExpression: current value is a string expression
        if (typeof currentValue === 'string') {
          const ctrl = new FormControl<string>(currentValue, { nonNullable: true });
          exprEntries.push({ key, ctrl });
        } else {
          const ctrl = new FormControl<number>((currentValue as number) ?? (defaultValue as number) ?? 0, {
            nonNullable: true,
          });
          numberEntries.push({ key, ctrl, min: -100, max: 100, step: 0.1 });
        }
      }
    }

    // Also check current params for keys not in defaults (handle 'behavior', xStart/xEnd for area features, etc.)
    for (const key of Object.keys(params)) {
      if (key === 'of' || Object.keys(defaults).includes(key)) {
        continue;
      }

      const value = params[key];

      if (key in this.SELECT_PARAM_OPTIONS) {
        selKeys.push(key);
        const ctrl = this.getSelectCtrl(key);
        ctrl.setValue((value as string) ?? '', { emitEvent: false });
      } else if (typeof value === 'string') {
        // String ValueExpression (e.g. xStart: "e.x0 + 1")
        const ctrl = new FormControl<string>(value, { nonNullable: true });
        exprEntries.push({ key, ctrl });
      } else if (typeof value === 'number') {
        // Numeric param not in defaults (e.g. xStart: 5 for functionArea)
        const ctrl = new FormControl<number>(value, { nonNullable: true });
        numberEntries.push({ key, ctrl, min: -100, max: 100, step: 0.1 });
      } else if (typeof value === 'boolean') {
        boolKeys.push(key);
      }
    }

    this.numberParamEntries.set(numberEntries);
    this.expressionParamEntries.set(exprEntries);
    this.booleanParamKeys.set(boolKeys);
    this.selectParamKeys.set(selKeys);
  }

  private buildPrimitiveParamCtrls(def: PrimitiveElementDef): void {
    this.numberParamEntries.set([]);
    this.expressionParamEntries.set([]);
    this.booleanParamKeys.set([]);
    this.selectParamKeys.set([]);
    const exprModes = new Set<string>();

    switch (def.type) {
      case 'label': {
        const p = def.params as LabelElementParams;
        this.primLabelTextCtrl.setValue(p.text ?? '', { emitEvent: false });
        this.primLabelPrecisionCtrl.setValue(p.precision ?? null, { emitEvent: false });
        this.primLabelOfCtrl.setValue(p.of ?? '', { emitEvent: false });
        if (typeof p.position?.x === 'string') {
          exprModes.add('posX');
          this.getPrimExprCtrl('posX').setValue(p.position.x, { emitEvent: false });
        } else {
          this.primLabelPosXCtrl.setValue(typeof p.position?.x === 'number' ? p.position.x : 0, { emitEvent: false });
        }
        if (typeof p.position?.y === 'string') {
          exprModes.add('posY');
          this.getPrimExprCtrl('posY').setValue(p.position.y, { emitEvent: false });
        } else {
          this.primLabelPosYCtrl.setValue(typeof p.position?.y === 'number' ? p.position.y : 0, { emitEvent: false });
        }
        break;
      }
      case 'point': {
        const p = def.params as PointElementParams;
        if (typeof p.x === 'string') {
          exprModes.add('pointX');
          this.getPrimExprCtrl('pointX').setValue(p.x, { emitEvent: false });
        } else {
          this.primPointXCtrl.setValue(typeof p.x === 'number' ? p.x : 0, { emitEvent: false });
        }
        if (typeof p.y === 'string') {
          exprModes.add('pointY');
          this.getPrimExprCtrl('pointY').setValue(p.y, { emitEvent: false });
        } else {
          this.primPointYCtrl.setValue(typeof p.y === 'number' ? p.y : 0, { emitEvent: false });
        }
        this.primPointNameCtrl.setValue(p.name ?? '', { emitEvent: false });
        break;
      }
      case 'line': {
        const p = def.params as LineElementParams;
        if (typeof p.from?.x === 'string') {
          exprModes.add('fromX');
          this.getPrimExprCtrl('fromX').setValue(p.from.x, { emitEvent: false });
        } else {
          this.primLineFromXCtrl.setValue(typeof p.from?.x === 'number' ? p.from.x : 0, { emitEvent: false });
        }
        if (typeof p.from?.y === 'string') {
          exprModes.add('fromY');
          this.getPrimExprCtrl('fromY').setValue(p.from.y, { emitEvent: false });
        } else {
          this.primLineFromYCtrl.setValue(typeof p.from?.y === 'number' ? p.from.y : 0, { emitEvent: false });
        }
        if (typeof p.to?.x === 'string') {
          exprModes.add('toX');
          this.getPrimExprCtrl('toX').setValue(p.to.x, { emitEvent: false });
        } else {
          this.primLineToXCtrl.setValue(typeof p.to?.x === 'number' ? p.to.x : 0, { emitEvent: false });
        }
        if (typeof p.to?.y === 'string') {
          exprModes.add('toY');
          this.getPrimExprCtrl('toY').setValue(p.to.y, { emitEvent: false });
        } else {
          this.primLineToYCtrl.setValue(typeof p.to?.y === 'number' ? p.to.y : 0, { emitEvent: false });
        }
        this.primLineNameCtrl.setValue(p.name ?? '', { emitEvent: false });
        break;
      }
      case 'polygon': {
        // Polygon vertices are edited via JSON; no individual param controls needed
        break;
      }
    }
    this.primExpressionModes.set(exprModes);
  }

  private syncDisplayCtrls(def: ElementDef, display: ElementDisplayConfig | undefined): void {
    const category = isFunctionElementDef(def) ? 'function' : isPrimitiveElementDef(def) ? 'primitive' : 'feature';
    const meta = getDisplayStyleMeta(def.type, category);
    const key = meta.primaryKey;
    const styleSection = display?.[key] as Record<string, unknown> | undefined;

    // Color (primary — for polygon this is fillColor, otherwise color from primaryKey)
    const color =
      key === 'polygon' ? ((styleSection?.['fillColor'] ?? '') as string) : ((styleSection?.['color'] ?? '') as string);
    this.colorCtrl.setValue(color, { emitEvent: false });

    // Secondary color (line/curve color when primary is polygon)
    if (meta.properties.includes('color') && (meta.secondaryKeys?.length ?? 0) > 0) {
      const colorKey = getStyleKeyForProperty('color', meta);
      const colorSection = display?.[colorKey] as Record<string, unknown> | undefined;
      this.secondaryColorCtrl.setValue((colorSection?.['color'] as string) ?? '', { emitEvent: false });
    }

    // Stroke width — read from the correct style key
    if (meta.properties.includes('strokeWidth')) {
      const swKey = getStyleKeyForProperty('strokeWidth', meta);
      const swSection = display?.[swKey] as Record<string, unknown> | undefined;
      this.strokeWidthCtrl.setValue((swSection?.['strokeWidth'] as number) ?? 2, { emitEvent: false });
    }

    // Dash — read from the correct style key
    if (meta.properties.includes('dash')) {
      const dashKey = getStyleKeyForProperty('dash', meta);
      const dashSection = display?.[dashKey] as Record<string, unknown> | undefined;
      this.dashCtrl.setValue((dashSection?.['dash'] as number) ?? 0, { emitEvent: false });
    }

    // Size (points)
    if (meta.properties.includes('size')) {
      this.sizeCtrl.setValue((styleSection?.['size'] as number) ?? 4, { emitEvent: false });
    }

    // Fill opacity (polygons)
    if (meta.properties.includes('fillOpacity')) {
      this.fillOpacityCtrl.setValue((styleSection?.['fillOpacity'] as number) ?? 0.3, { emitEvent: false });
    }

    // Point extras
    if (meta.primaryKey === 'point' && meta.properties.includes('face')) {
      const pointSection = display?.point as Record<string, unknown> | undefined;
      this.faceCtrl.setValue((pointSection?.['face'] as string) ?? '', { emitEvent: false });
      this.strokeColorCtrl.setValue((pointSection?.['strokeColor'] as string) ?? '', { emitEvent: false });
    }

    // Polygon extras
    if (meta.primaryKey === 'polygon' && meta.properties.includes('strokeColor')) {
      const polySection = display?.polygon as Record<string, unknown> | undefined;
      this.polyStrokeColorCtrl.setValue((polySection?.['strokeColor'] as string) ?? '', { emitEvent: false });
      this.polyStrokeWidthCtrl.setValue((polySection?.['strokeWidth'] as number) ?? 1, { emitEvent: false });
    }

    // Primitive label display
    if (key === 'label') {
      const labelSection = display?.label as Record<string, unknown> | undefined;
      this.primLabelVariantCtrl.setValue((labelSection?.['variant'] as string) ?? '', { emitEvent: false });
      this.primLabelPositionCtrl.setValue((labelSection?.['position'] as string) ?? '', { emitEvent: false });
      this.primLabelTextAlignCtrl.setValue((labelSection?.['textAlign'] as string) ?? '', { emitEvent: false });
      this.primLabelFontSizeCtrl.setValue((labelSection?.['fontSize'] as number) ?? null, { emitEvent: false });
    }

    // Label config
    if (meta.hasLabel) {
      const labelKey = meta.labelKey ?? key;
      const section = display?.[labelKey] as Record<string, unknown> | undefined;
      const label = section?.['label'];
      const labelConfig: ElementLabelConfig =
        typeof label === 'object' && label !== null ? (label as ElementLabelConfig) : {};
      this.labelPositionCtrl.setValue((labelConfig.position as string) ?? '', { emitEvent: false });
      this.labelVariantCtrl.setValue((labelConfig.variant as string) ?? '', { emitEvent: false });
      this.labelContentVariantCtrl.setValue((labelConfig.contentVariant as string) ?? '', { emitEvent: false });
      this.labelTextCtrl.setValue(labelConfig.text ?? '', { emitEvent: false });
      this.labelFontSizeCtrl.setValue(labelConfig.fontSize ?? null, { emitEvent: false });
      this.labelPrecisionCtrl.setValue(labelConfig.precision ?? null, { emitEvent: false });
      this.labelTextAlignCtrl.setValue((labelConfig.textAlign as string) ?? '', { emitEvent: false });
      this.labelColorCtrl.setValue((labelConfig.color as string) ?? '', { emitEvent: false });
      const offset = labelConfig.offset ?? [0, 0];
      this.labelOffsetXCtrl.setValue(offset[0], { emitEvent: false });
      this.labelOffsetYCtrl.setValue(offset[1], { emitEvent: false });
      const anchorAt = labelConfig.anchorAt;
      this.labelAnchorAtCtrl.setValue(typeof anchorAt === 'string' ? anchorAt : '', { emitEvent: false });
    }

    // Snapping (point elements)
    if (meta.primaryKey === 'point') {
      const pointSection = display?.point as Record<string, unknown> | undefined;
      const snapping = pointSection?.['snapping'] as SnappingConfig | undefined;
      this.snapGridSizeXCtrl.setValue(snapping?.grid?.sizeX ?? 1, { emitEvent: false });
      this.snapGridSizeYCtrl.setValue(snapping?.grid?.sizeY ?? 1, { emitEvent: false });
      const attractorIds = snapping?.attractors?.attractors ?? [];
      this.snapAttractorsCtrl.setValue(attractorIds.filter((a): a is string => typeof a === 'string').join(', '), {
        emitEvent: false,
      });
      this.snapDistanceCtrl.setValue(snapping?.attractors?.distance ?? 0, { emitEvent: false });
      this.snapSnatchDistanceCtrl.setValue(snapping?.attractors?.snatchDistance ?? 0, { emitEvent: false });
      this.snapUnitCtrl.setValue(snapping?.attractors?.unit ?? 'user', { emitEvent: false });
    }
  }

  private emitStyleChange(property: string, value: number): void {
    const display = this.elementDisplay() ?? {};
    const key = getStyleKeyForProperty(property, this.styleMeta());
    this.displayChange.emit({
      ...display,
      [key]: { ...(display[key] ?? {}), [property]: value },
    });
  }

  private emitSnapGridChange(partial: Partial<SnappingConfig['grid'] & object>): void {
    const display = this.elementDisplay() ?? {};
    const point = (display.point ?? {}) as Record<string, unknown>;
    const snapping = (point['snapping'] ?? {}) as SnappingConfig;
    this.displayChange.emit({
      ...display,
      point: {
        ...point,
        snapping: {
          ...snapping,
          grid: { ...(snapping.grid ?? { enabled: true }), ...partial },
        },
      },
    });
  }

  private emitSnapAttractorsChange(partial: Partial<SnappingConfig['attractors'] & object>): void {
    const display = this.elementDisplay() ?? {};
    const point = (display.point ?? {}) as Record<string, unknown>;
    const snapping = (point['snapping'] ?? {}) as SnappingConfig;
    this.displayChange.emit({
      ...display,
      point: {
        ...point,
        snapping: {
          ...snapping,
          attractors: { ...(snapping.attractors ?? { attractors: [] }), ...partial },
        },
      },
    });
  }
}
